// @ts-strict-ignore
import { useApolloClient } from "@apollo/client";
import { MetadataIdSchema } from "@dashboard/components/Metadata";
import NotFoundPage from "@dashboard/components/NotFoundPage";
import { Task } from "@dashboard/containers/BackgroundTasks/types";
import {
  JobStatusEnum,
  OrderCustomerNotificationTypeEnum,
  OrderDetailsWithMetadataDocument,
  OrderPaymentReminderModeEnum,
  OrderStatus,
  useOrderConfirmMutation,
  useOrderSendNotificationMutation,
  useUpdateMetadataMutation,
  useUpdatePrivateMetadataMutation,
} from "@dashboard/graphql";
import useBackgroundTask from "@dashboard/hooks/useBackgroundTask";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { createOrderMetadataIdSchema } from "@dashboard/orders/components/OrderDetailsPage/utils";
import {
  INVOICE_MARKED_DONE_KEY,
  ORDER_RECEIVED_MARKED_KEY,
} from "@dashboard/orders/components/OrderProgressStepper/utils";
import {
  generatePickupPin,
  getPickupPin,
  PICKUP_PIN_KEY,
  PICKUP_READY_NOTIFIED_KEY,
} from "@dashboard/orders/utils/delivery";
import getOrderErrorMessage from "@dashboard/utils/errors/order";
import createDialogActionHandlers from "@dashboard/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@dashboard/utils/handlers/metadataUpdateHandler";
import { useIntl } from "react-intl";

import OrderOperations from "../../containers/OrderOperations";
import { orderListUrl, orderUrl, OrderUrlDialog, OrderUrlQueryParams } from "../../urls";
import { ORDER_NIF_KEY } from "../../utils/nif";
import { OrderDetailsMessages } from "./OrderDetailsMessages";
import { OrderDraftDetails } from "./OrderDraftDetails";
import { OrderNormalDetails } from "./OrderNormalDetails";
import { OrderUnconfirmedDetails } from "./OrderUnconfirmedDetails";
import { useOrderDetails } from "./useOrderDetails";

interface OrderDetailsProps {
  id: string;
  params: OrderUrlQueryParams;
}

const OrderDetails = ({ id, params }: OrderDetailsProps) => {
  const navigate = useNavigator();
  const { queue } = useBackgroundTask();
  const intl = useIntl();
  const [updateMetadata, updateMetadataOpts] = useUpdateMetadataMutation({});
  const [updatePrivateMetadata, updatePrivateMetadataOpts] = useUpdatePrivateMetadataMutation({});
  const notify = useNotifier();
  const apolloClient = useApolloClient();
  const [openModal, closeModal] = createDialogActionHandlers<OrderUrlDialog, OrderUrlQueryParams>(
    navigate,
    params => orderUrl(id, params),
    params,
    ["type"],
  );
  const handleBack = () => navigate(orderListUrl());
  const [orderConfirm] = useOrderConfirmMutation({
    onCompleted: ({ orderConfirm: { errors } }) => {
      const isError = !!errors.length;

      notify({
        status: isError ? "error" : "success",
        text: isError ? getOrderErrorMessage(errors[0], intl) : "Confirmed Order",
      });
    },
  });

  const [orderSendNotification] = useOrderSendNotificationMutation({
    onCompleted: ({ orderSendNotification: result }) => {
      const isError = !!result?.errors?.length;

      notify({
        status: isError ? "error" : "success",
        text: isError
          ? getOrderErrorMessage(result.errors[0], intl)
          : intl.formatMessage(commonMessages.savedChanges),
      });
    },
  });

  const { data, loading } = useOrderDetails(id);

  const order = data?.order;

  if (order === null) {
    return <NotFoundPage onBack={handleBack} />;
  }

  const isOrderUnconfirmed = order?.status === OrderStatus.UNCONFIRMED;
  const isOrderDraft = order?.status === OrderStatus.DRAFT;
  const handleSubmit = async (data: MetadataIdSchema | null, notifyCustomer = true) => {
    if (order?.status === OrderStatus.UNCONFIRMED) {
      await orderConfirm({ variables: { id: order?.id, notifyCustomer } });
    }

    const initial = createOrderMetadataIdSchema(order);
    const metadataPromises = Object.entries(initial).map(([id, metaEntry]) => {
      const update = createMetadataUpdateHandler(
        { ...metaEntry, id },
        () => Promise.resolve([]),
        variables => updateMetadata({ variables }),
        variables => updatePrivateMetadata({ variables }),
      );

      // When confirming straight from the stepper there is no edited form data,
      // so fall back to the order's current metadata (a no-op update).
      return update(data?.[id] ?? metaEntry);
    });
    const result = await Promise.all(metadataPromises);
    const errors = result.reduce((p, c) => p.concat(c), []);

    if (errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });
    }

    return result;
  };

  // With no invoicing app installed, `invoiceRequest` always fails, so staff
  // instead flag the invoice step done by hand. The flag lives in order metadata
  // so it persists and is shared; `marked === false` clears it (undo).
  const handleInvoiceMarkToggle = async (marked: boolean) => {
    const { data: result } = await updateMetadata({
      variables: {
        id,
        input: marked ? [{ key: INVOICE_MARKED_DONE_KEY, value: "true" }] : [],
        keysToDelete: marked ? [] : [INVOICE_MARKED_DONE_KEY],
      },
    });

    if (result?.updateMetadata?.errors?.length) {
      return;
    }

    await apolloClient.refetchQueries({
      include: [OrderDetailsWithMetadataDocument],
    });
    notify({
      status: "success",
      text: intl.formatMessage(commonMessages.savedChanges),
    });
  };

  // Re-sends a customer email that Saleor has no native resend for (order
  // confirmation, payment confirmation, a delivery notice, or a payment
  // reminder) via the custom backend `orderSendNotification` mutation.
  // `paymentReminderMode` is only read by the backend when notificationType is
  // PAYMENT_REMINDER.
  const handleSendNotification = (
    notificationType: OrderCustomerNotificationTypeEnum,
    paymentReminderMode?: OrderPaymentReminderModeEnum,
  ) => orderSendNotification({ variables: { id, notificationType, paymentReminderMode } });

  // Marks a manual stepper step (the final "received" step, or "ready for pickup"
  // for in-store pickup) done. Like the invoice mark, the choice lives in order
  // metadata so it persists and is shared; `marked === false` clears it (undo).
  // The optional customer email is sent separately by the view (via
  // `onSendNotification`).
  const handleStepFlagToggle = async (key: string, marked: boolean) => {
    const { data: result } = await updateMetadata({
      variables: {
        id,
        input: marked ? [{ key, value: "true" }] : [],
        keysToDelete: marked ? [] : [key],
      },
    });

    if (result?.updateMetadata?.errors?.length) {
      return;
    }

    await apolloClient.refetchQueries({
      include: [OrderDetailsWithMetadataDocument],
    });
    notify({
      status: "success",
      text: intl.formatMessage(commonMessages.savedChanges),
    });
  };
  const handleReceivedMarkToggle = (marked: boolean) =>
    handleStepFlagToggle(ORDER_RECEIVED_MARKED_KEY, marked);
  // Marking an in-store pickup order ready also gives it the pickup PIN (kept
  // on undo) that the pickup ticket prints and the "ready" email sends.
  const handlePickupReadyMarkToggle = async (marked: boolean) => {
    if (marked && !getPickupPin(order?.privateMetadata)) {
      await updatePrivateMetadata({
        variables: {
          id,
          input: [{ key: PICKUP_PIN_KEY, value: generatePickupPin() }],
          keysToDelete: [],
        },
      });
    }

    return handleStepFlagToggle(PICKUP_READY_NOTIFIED_KEY, marked);
  };

  // `orderCancel` takes no reason argument, so the staff-selected reason is
  // persisted dashboard-side: as a `cancellationReason` metadata entry (queryable,
  // shared) and, by the caller, as an order-history note. The metadata entry is
  // only written when staff opts to share it with the customer (notifyReason) —
  // this is the key the cancellation email template reads to decide whether to
  // include the reason.
  const handleSaveCancellationReason = async (reason: string, notifyReason: boolean) => {
    if (!reason || !notifyReason) {
      return;
    }

    await updateMetadata({
      variables: {
        id,
        input: [{ key: "cancellationReason", value: reason }],
        keysToDelete: [],
      },
    });
  };
  // The customer's VAT number (NIF) is captured while editing the billing
  // address, but it is not part of Saleor's AddressInput. It is persisted as an
  // order metadata entry under the same `nif` key the storefront and email
  // templates use. An empty value clears the entry.
  const handleBillingNifUpdate = async (nif: string) => {
    const trimmedNif = nif.trim();
    const { data: result } = await updateMetadata({
      variables: {
        id,
        input: trimmedNif ? [{ key: ORDER_NIF_KEY, value: trimmedNif }] : [],
        keysToDelete: trimmedNif ? [] : [ORDER_NIF_KEY],
      },
    });

    if (result?.updateMetadata?.errors?.length) {
      return;
    }

    await apolloClient.refetchQueries({
      include: [OrderDetailsWithMetadataDocument],
    });
    notify({
      status: "success",
      text: intl.formatMessage(commonMessages.savedChanges),
    });
  };

  return (
    <OrderDetailsMessages id={id} params={params}>
      {orderMessages => (
        <OrderOperations
          order={id}
          onNoteAdd={orderMessages.handleNoteAdd}
          onNoteUpdate={orderMessages.handleNoteUpdate}
          onOrderCancel={orderMessages.handleOrderCancel}
          onOrderVoid={orderMessages.handleOrderVoid}
          onPaymentCapture={orderMessages.handlePaymentCapture}
          onUpdate={orderMessages.handleUpdate}
          onDraftUpdate={orderMessages.handleDraftUpdate}
          onShippingMethodUpdate={data => {
            orderMessages.handleShippingMethodUpdate(data);
            order.total = data.orderUpdateShipping.order.total;
          }}
          onOrderLineDelete={orderMessages.handleOrderLineDelete}
          onOrderLinesAdd={orderMessages.handleOrderLinesAdd}
          onOrderLineUpdate={orderMessages.handleOrderLineUpdate}
          onOrderFulfillmentApprove={orderMessages.handleOrderFulfillmentApprove}
          onOrderFulfillmentCancel={orderMessages.handleOrderFulfillmentCancel}
          onOrderFulfillmentUpdate={orderMessages.handleOrderFulfillmentUpdate}
          onDraftFinalize={orderMessages.handleDraftFinalize}
          onDraftCancel={orderMessages.handleDraftCancel}
          onOrderMarkAsPaid={orderMessages.handleOrderMarkAsPaid}
          onInvoiceRequest={data => {
            if (data.invoiceRequest.invoice.status === JobStatusEnum.SUCCESS) {
              orderMessages.handleInvoiceGenerateFinished(data);
            } else {
              orderMessages.handleInvoiceGeneratePending(data);
              queue(Task.INVOICE_GENERATE, {
                generateInvoice: {
                  invoiceId: data.invoiceRequest.invoice.id,
                  orderId: id,
                },
              });
            }
          }}
          onInvoiceSend={orderMessages.handleInvoiceSend}
          onTransactionActionSend={orderMessages.handleTransactionAction}
          onManualTransactionAdded={async data => {
            await apolloClient.refetchQueries({
              include: [OrderDetailsWithMetadataDocument],
            });
            orderMessages.handleAddManualTransaction(data);
          }}
        >
          {({
            orderAddNote,
            orderUpdateNote,
            orderCancel,
            orderDraftUpdate,
            orderLinesAdd,
            orderLineDelete,
            orderLineUpdate,
            orderPaymentCapture,
            orderVoid,
            orderShippingMethodUpdate,
            orderUpdate,
            orderFulfillmentApprove,
            orderFulfillmentCancel,
            orderFulfillmentUpdateTracking,
            orderDraftCancel,
            orderDraftFinalize,
            orderPaymentMarkAsPaid,
            orderInvoiceRequest,
            orderInvoiceSend,
            orderTransactionAction,
            orderAddManualTransaction,
          }) => (
            <>
              {!isOrderDraft && !isOrderUnconfirmed && (
                <OrderNormalDetails
                  id={id}
                  params={params}
                  loading={loading}
                  data={data}
                  orderAddNote={orderAddNote}
                  orderUpdateNote={orderUpdateNote}
                  orderInvoiceRequest={orderInvoiceRequest}
                  onInvoiceMarkToggle={handleInvoiceMarkToggle}
                  onReceivedMarkToggle={handleReceivedMarkToggle}
                  onPickupReadyMarkToggle={handlePickupReadyMarkToggle}
                  onSaveCancellationReason={handleSaveCancellationReason}
                  onSendNotification={handleSendNotification}
                  handleSubmit={handleSubmit}
                  orderUpdate={orderUpdate}
                  orderShippingMethodUpdate={orderShippingMethodUpdate}
                  orderCancel={orderCancel}
                  orderPaymentMarkAsPaid={orderPaymentMarkAsPaid}
                  orderVoid={orderVoid}
                  orderPaymentCapture={orderPaymentCapture}
                  orderLinesAdd={orderLinesAdd}
                  orderLineUpdate={orderLineUpdate}
                  orderLineDelete={orderLineDelete}
                  orderFulfillmentApprove={orderFulfillmentApprove}
                  orderFulfillmentCancel={orderFulfillmentCancel}
                  orderFulfillmentUpdateTracking={orderFulfillmentUpdateTracking}
                  orderInvoiceSend={orderInvoiceSend}
                  orderTransactionAction={orderTransactionAction}
                  orderAddManualTransaction={orderAddManualTransaction}
                  updateMetadataOpts={updateMetadataOpts}
                  updatePrivateMetadataOpts={updatePrivateMetadataOpts}
                  onBillingNifUpdate={handleBillingNifUpdate}
                  openModal={openModal}
                  closeModal={closeModal}
                />
              )}
              {isOrderDraft && (
                <OrderDraftDetails
                  id={id}
                  params={params}
                  loading={loading}
                  data={data}
                  orderAddNote={orderAddNote}
                  orderUpdateNote={orderUpdateNote}
                  orderLineUpdate={orderLineUpdate}
                  orderLineDelete={orderLineDelete}
                  orderShippingMethodUpdate={orderShippingMethodUpdate}
                  orderLinesAdd={orderLinesAdd}
                  orderPaymentMarkAsPaid={orderPaymentMarkAsPaid}
                  orderDraftUpdate={orderDraftUpdate}
                  orderDraftCancel={orderDraftCancel}
                  orderDraftFinalize={orderDraftFinalize}
                  onBillingNifUpdate={handleBillingNifUpdate}
                  openModal={openModal}
                  closeModal={closeModal}
                />
              )}
              {isOrderUnconfirmed && (
                <OrderUnconfirmedDetails
                  id={id}
                  params={params}
                  data={data}
                  orderAddNote={orderAddNote}
                  orderUpdateNote={orderUpdateNote}
                  orderLineUpdate={orderLineUpdate}
                  orderLineDelete={orderLineDelete}
                  orderInvoiceRequest={orderInvoiceRequest}
                  onInvoiceMarkToggle={handleInvoiceMarkToggle}
                  onReceivedMarkToggle={handleReceivedMarkToggle}
                  onSaveCancellationReason={handleSaveCancellationReason}
                  onSendNotification={handleSendNotification}
                  handleSubmit={handleSubmit}
                  orderUpdate={orderUpdate}
                  orderCancel={orderCancel}
                  orderShippingMethodUpdate={orderShippingMethodUpdate}
                  orderLinesAdd={orderLinesAdd}
                  orderPaymentMarkAsPaid={orderPaymentMarkAsPaid}
                  orderVoid={orderVoid}
                  orderPaymentCapture={orderPaymentCapture}
                  orderFulfillmentApprove={orderFulfillmentApprove}
                  orderFulfillmentCancel={orderFulfillmentCancel}
                  orderFulfillmentUpdateTracking={orderFulfillmentUpdateTracking}
                  orderInvoiceSend={orderInvoiceSend}
                  updateMetadataOpts={updateMetadataOpts}
                  updatePrivateMetadataOpts={updatePrivateMetadataOpts}
                  orderTransactionAction={orderTransactionAction}
                  orderAddManualTransaction={orderAddManualTransaction}
                  onBillingNifUpdate={handleBillingNifUpdate}
                  openModal={openModal}
                  closeModal={closeModal}
                />
              )}
            </>
          )}
        </OrderOperations>
      )}
    </OrderDetailsMessages>
  );
};

export default OrderDetails;
